import { Model, Factory } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { ModelInstance, Collection } from 'miragejs'
import { randNumber } from '@ngneat/falso'

import type { SchemaAttributes } from '../types'
import { sortable, pageable, createErrorResponse } from './utils.js'
import type { MirageServer, MockServer } from './server'

export type AccountNumberData = SchemaAttributes<'AccountNumber'>

export const model = Model as ModelDefinition<AccountNumberData>

export const factory = Factory.extend({
  number() {
    return `${randNumber({ min: 1000, max: 9999 })}`
  },
  description() {
    const descriptions = [
      'Sales Revenue',
      'Service Income',
      'Consultation Fees',
      'Medication Sales',
      'Laboratory Services',
      'Surgical Procedures',
      'Boarding Fees',
      'Grooming Services',
      'Vaccination Income',
      'Emergency Services',
    ]
    return descriptions[randNumber({ min: 0, max: descriptions.length - 1 })]
  },
})

export function routes(server: MockServer) {
  /**
   * GET /organization/account-numbers/v1
   */
  server.get(
    '/organization/account-numbers/v1',
    function (this: MirageServer, schema, request) {
      const allAccountNumbers = schema.all('accountNumber') as Collection<
        ModelInstance<AccountNumberData>
      >

      // 1) Sort
      const sorted = sortable(allAccountNumbers, request)

      // 2) Paginate
      const { data, meta } = pageable(sorted, request)

      return {
        data: data.models.map((accountNumber) => {
          return {
            id: accountNumber.id,
            type: 'AccountNumber',
            attributes: {
              number: accountNumber.attrs.number,
              description: accountNumber.attrs.description,
            },
          }
        }),
        meta: {
          pagination: meta,
        },
      }
    },
  )

  /**
   * GET /organization/account-numbers/v1/:id
   */
  server.get('/organization/account-numbers/v1/:id', (schema, request) => {
    const id = request.params.id
    const accountNumber = schema.find(
      'accountNumber',
      id,
    ) as ModelInstance<AccountNumberData> | null

    if (!accountNumber) {
      return createErrorResponse({
        status: 404,
        errors: [{ detail: `AccountNumber with id ${id} not found` }],
      })
    }

    return {
      data: {
        id: accountNumber.id,
        type: 'AccountNumber',
        attributes: {
          number: accountNumber.attrs.number,
          description: accountNumber.attrs.description,
        },
      },
    }
  })
}

export function seeds(server: MockServer) {
  server.createList('accountNumber', 15)
}
